import { 
    ref, 
    off,
    onChildAdded, 
    onChildChanged, 
    onChildRemoved, 
    push,
    set,
    get,
    remove,
    DatabaseReference,
} from "firebase/database";
import { useEffect, useRef, useState } from "react";
import { 
    ExportState,
    Dimensions, 
    Layer,
    ProjectState, 
    DetectedObject,
    ObjectAdjustments,
    Adjustments,
    ProjectAnimation
} from "../types";
import { getUserId, useAuth } from "./auth";
import { database } from "./firebase";
import { downloadDetectedObjects } from "./storage";
import { StoryState } from "../types/Story";

type CreateUserProject = (projectId: string) => void;

export const createUserProject: CreateUserProject = (projectId) => {
    const userId = getUserId();
    const userProjectListRef = ref(database, `userProjects/${userId}`);
    const userProjectRef = push(userProjectListRef);
    // { userId : { key1: projectId1, key2: projectId2 } }
    set(userProjectRef, projectId);
}

type CreateEmptyProject = () => string | null;

export const createEmptyProject: CreateEmptyProject = () => {
    try {
        const projectListRef = ref(database, `project/`);
        const projectRef = push(projectListRef);
        if (projectRef.key) {
            createUserProject(projectRef.key);
        }
        return projectRef.key;
    } catch (e) {
        console.error(e);
    }
    return null;
}

type CreateProject = (projectState: ProjectState) => string | null;

export const createProject: CreateProject = (projectState: ProjectState) => {
    const userId = getUserId();
    try {
        const projectListRef = ref(database, `project/`);
        let projectRef = ref(database, `project/${projectState.projectId}`);
        if (!projectState.projectId) {
            projectRef = push(projectListRef);
            projectState.projectId = projectRef.key || undefined;
        }
        if (projectState.projectId) {
            createUserProject(projectState.projectId);
            projectState.ownerId = userId;
        }
        set(projectRef, projectState);
        return projectRef.key;
    } catch (e) {
        console.error(e);
    }
    return null;
}


type GetProject = (
    projectId: string,
) => Promise<ProjectState>;

export const getProject: GetProject = (projectId) => {
    return new Promise((resolve, reject) => {
        const projectRef = ref(database, `project/${projectId}`);
        get(projectRef).then(snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                resolve(data);
            } else {
                reject();
            }
        });
    })
}

type UpdateProject = (projectState: ProjectState) => void;

export const updateProject: UpdateProject = (projectState) => {
    try {
        projectState.layers = projectState.layers.map(layer => ({
            ...layer,
            detectedObjects: undefined
        }));
        const projectRef = ref(database, `project/${projectState.projectId}`);
        if (!projectState.ownerId) {
            projectState.ownerId = getUserId();
        }
        set(projectRef, projectState);
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectStackLength = (
    projectState: ProjectState,
    stackLength: number
) => void;

export const updateProjectStackLength: UpdateProjectStackLength = (
    projectState,
    stackLength
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.stackLength = stackLength;
            let maxFrame = 0;
            project.layers = project.layers.map(layer => {
                if (layer.type !== "image") {
                    return layer;
                }
                maxFrame = Math.max(layer.startFrame, maxFrame);
                return {
                    ...layer,
                    endFrame: layer.startFrame + stackLength,
                    blendMode: stackLength === 0 ? "normal" : "lighten"
                };
            });
            project.durationInFrames = Math.round(maxFrame - stackLength);
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectSliceLength = (
    projectState: ProjectState,
    sliceLength: number
) => void;

export const updateProjectSliceLength: UpdateProjectSliceLength = (
    projectState,
    sliceLength
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.sliceLength = sliceLength;
            let maxFrame = 0;
            project.layers = project.layers.map(layer => {
                if (layer.type !== "image") {
                    return layer;
                }
                maxFrame = Math.max(layer.endFrame, maxFrame);
                return {
                    ...layer,
                    startFrame: Math.max(layer.endFrame - sliceLength, 0),
                    blendMode: "normal"
                };
            });
            project.durationInFrames = Math.round(maxFrame - sliceLength);
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectRotation = (
    projectState: ProjectState,
    rotation: number
) => void;

export const updateProjectRotation: UpdateProjectRotation = (
    projectState,
    rotation
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.layers = project.layers.map(layer => {
                return {
                    ...layer,
                    rotation
                };
            });
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectAnimation = (
    projectState: ProjectState,
    animation: ProjectAnimation | undefined
) => void;

export const updateProjectAnimation: UpdateProjectAnimation = (
    projectState,
    animation
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            if (!animation) {
                delete project.animation;
            } else {
                project.animation = animation;
            }
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectFps = (
    projectState: ProjectState,
    fps: number
) => void;

export const updateProjectFps: UpdateProjectFps = (
    projectState,
    fps
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.fps = fps;
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectAdjustments = (
    projectState: ProjectState,
    adjustments: Adjustments
) => void;

export const updateProjectAdjustments: UpdateProjectAdjustments = (
    projectState,
    adjustments
) => {
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.layers = project.layers.map(layer => ({
                ...layer,
                adjustments
            }));
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type UpdateProjectObjectAdjustments = (
    projectState: ProjectState,
    objectAdjustments: ObjectAdjustments
) => void;

export const updateProjectObjectAdjustments: UpdateProjectObjectAdjustments = (
    projectState,
    objectAdjustments
) => {
    console.log('updateProjectObjectAdjustments', `project/${projectState.projectId}`, objectAdjustments)
    try {
        const projectRef = ref(database, `project/${projectState.projectId}`);
        get(projectRef).then(snapshot => {
            const project: ProjectState = snapshot.val();
            project.objectAdjustments = objectAdjustments;
            set(projectRef, project);
        });
    } catch (e) {
        console.error(e);
    }
}

type IsProjectOwner = (projectId: string) => Promise<boolean>;

const isProjectOwner: IsProjectOwner = (projectId) => {
    return new Promise((resolve, reject) => {
        getProject(projectId).then(projectState => {
            const userId = getUserId();
            const isOwner = !!userId && projectState.ownerId === userId;
            resolve(isOwner);
        })
    })
}


type DeleteProject = (
    projectId: string,
) => Promise<boolean>;

export const deleteProject: DeleteProject = (projectId) => {
    return new Promise(async (resolve, reject) => {
        const isOwner = await isProjectOwner(projectId);
        if (!isOwner) {
            reject("Only the project owner can delete the project");
            return;
        }
        const projectRef = ref(database, `project/${projectId}`);
        set(projectRef, null);
        resolve(true);
    })
}

type AddProjectLayer = (projectId: string, layer: Layer) => void;

export const addProjectLayer: AddProjectLayer = (projectId, layer) => {
    try {
        const projectRef = ref(database, `project/${projectId}`);
        get(projectRef).then(snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                data.layers.push(layer);
                set(projectRef, data);
            } else {
                console.log('project does not exist', projectId);
            }
        });
    } catch (e) {
        console.error(e);
    }
}


type DeleteProjectLayer = (projectId: string, layer: Layer) => void;

export const deleteProjectLayer: DeleteProjectLayer = (projectId, layer) => {
    try {
        const projectRef = ref(database, `project/${projectId}`);
        get(projectRef).then(snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const newLayers: Layer[] = [];
                data.layers.forEach((_layer: Layer) => {
                    if (_layer.id !== layer.id) {
                        newLayers.push(_layer);
                    }
                });
                data.layers = newLayers;
                set(projectRef, data);
            } else {
                console.log('project does not exist', projectId);
            }
        });
    } catch (e) {
        console.error(e);
    }
}

type QueueExport = (projectId?: string, storyId?: string) => Promise<string | null | undefined>;

export const queueExport: QueueExport = async (projectId, storyId) => {
    if (!projectId && !storyId) return;
    try {
        const exportQueueRef = ref(database, `export_queue/`);
        const exportRef = push(exportQueueRef);
        set(exportRef, {
            projectId: projectId || null,
            storyId: storyId || null,
        });
        return exportRef.key;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type SubscribeToRef = (
    ref: DatabaseReference,
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onRemove: (key: string | null) => void
) => void;

export const subscribeToRef: SubscribeToRef = (ref, onAdd, onChange, onRemove) => {
    onChildAdded(ref, (data) => {
        onAdd(data.key, data.val());
    });

    onChildChanged(ref, (data) => {
        onChange(data.key, data.val());
    });

    onChildRemoved(ref, (data) => {
        onRemove(data.key);
    });
}


type SubscribeToProject = (
    projectId: string,
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onRemove: (key: string | null) => void,
) => void;

export const subscribeToProject: SubscribeToProject = (projectId, onAdd, onChange, onRemove) => {
    const projectRef = ref(database, `project/${projectId}`);
    subscribeToRef(projectRef, onAdd, onChange, onRemove)
}

type UnsubscribeFromProject = (projectId: string) => void;

export const unsubscribeFromProject: UnsubscribeFromProject = (projectId) => {
    const projectRef = ref(database, `project/${projectId}`);
    off(projectRef);
}

export const injectDetectedObjectsToProject = async (
    detectedObjects: DetectedObject[],
    project: ProjectState
) => {
  if (!project.projectId) return project;
  if (!detectedObjects.length) return project;
  switch (project.type) {
    case "video":
        project.layers = project.layers.map(layer => ({
            ...layer,
            detectedObjects: layer.type === "video" ? detectedObjects : undefined
        }));
        break;
    case "timelapse":
        project.layers = project.layers.map(layer => ({
            ...layer,
            detectedObjects: layer.type === "image" ? detectedObjects.filter(({ frame }) => frame === layer.index) : undefined
        }))
        break;
  }
  return project;
}

type UseProject = (projectId: string) => {
    project: ProjectState | undefined,
    refresh: number
}

export const useProject: UseProject = (projectId) => {
    const [refresh, setRefresh] = useState<number>(Date.now());
    const project = useRef<ProjectState>();
    const detectedObjects = useRef<DetectedObject[]>([]);

    useEffect(() => {
        const injectDetectedObjects = async () => {
            if (!project.current?.projectId) return;
            if (detectedObjects.current.length < 1) {
                detectedObjects.current = await downloadDetectedObjects(project.current.projectId);
            }
            project.current = await injectDetectedObjectsToProject(detectedObjects.current, project.current);
            setRefresh(Date.now());
        }
        const onAdd = (key: any, val: any) => {
            if (!project.current) return;
            project.current = {
                ...project.current,
                [key]: val
            };
            setRefresh(Date.now());
        }
        const onChange = (key: any, val: any) => {
            if (!project.current) return;
            project.current = {
                ...project.current,
                [key]: val
            };
            setRefresh(Date.now());
            injectDetectedObjects();
        }
        const onRemove = (key: any) => {
            if (!project.current) return;
            project.current = {
                ...project.current,
                [key]: undefined
            };
            setRefresh(Date.now());
        }
        getProject(projectId).then(data => {
            project.current = data;
            subscribeToProject(projectId, onAdd, onChange, onRemove);
            injectDetectedObjects();
        })
        return () => {
            unsubscribeFromProject(projectId);
        }
    }, [projectId, setRefresh]);
    return {
        project: project.current, 
        refresh
    }
}

type SubscribeToProjects = (
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onRemove: (key: string | null) => void,
) => void;

export const subscribeToProjects: SubscribeToProjects = (onAdd, onChange, onRemove) => {
    const projectsRef = ref(database, `project`);
    subscribeToRef(projectsRef, onAdd, onChange, onRemove)
}

type UnsubscribeFromProjects = () => void;

export const unsubscribeFromProjects: UnsubscribeFromProjects = () => {
    const projectsRef = ref(database, `project`);
    off(projectsRef);
}

type UseProjects = (showAll?: boolean) => {
    projects: ProjectState[],
    refresh: number
}

export const useProjects: UseProjects = (showAll) => {
    const user = useAuth();
    const userId = user?.uid;
    const [refresh, setRefresh] = useState<number>(Date.now());
    const projects = useRef<ProjectState[]>([]);

    useEffect(() => {
        const onAdd = (_: any, val: any) => {
            if (!showAll && val.ownerId !== userId) {
                return;
            }
            projects.current.push(val);
            setRefresh(Date.now());
        }
        const onChange = (key: any, val: any) => {
            if (!showAll && val.ownerId !== userId) {
                return;
            }
            const _projects: any = [];
            for (let i = 0; i < projects.current.length; i++) {
                const project = projects.current[i];
                if (project.projectId === key) {
                    _projects.push(val);
                    continue;
                }
                _projects.push(project);
            }
            projects.current = _projects;
            setRefresh(Date.now());
        }
        const onRemove = (key: any) => {
            const _projects: any = [];
            for (let i = 0; i < projects.current.length; i++) {
                const project = projects.current[i];
                if (project.projectId === key) {
                    continue;
                }
                _projects.push(project);
            }
            projects.current = _projects;
            setRefresh(Date.now());
        }
        subscribeToProjects(onAdd, onChange, onRemove);
        return () => {
            unsubscribeFromProjects();
        }
    }, [setRefresh, userId, showAll])
    return {
        projects: projects.current, 
        refresh
    }
}


type CreateStory = (storyState: StoryState) => string | null;

export const createStory: CreateStory = (storyState: StoryState) => {
    const userId = getUserId();
    try {
        const storyListRef = ref(database, `story/`);
        let storyRef = ref(database, `story/${storyState.storyId}`);
        if (!storyState.storyId) {
            storyRef = push(storyListRef);
            storyState.storyId = storyRef.key || `${Date.now()}`;
        }
        storyState.ownerId = userId;
        set(storyRef, storyState);
        return storyRef.key;
    } catch (e) {
        console.error(e);
    }
    return null;
}


type GetStory = (
    projectId: string,
) => Promise<StoryState>;

export const getStory: GetStory = (storyId) => {
    return new Promise((resolve, reject) => {
        const storyRef = ref(database, `story/${storyId}`);
        get(storyRef).then(snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                resolve(data);
            } else {
                reject();
            }
        });
    })
}

type SubscribeToStory = (
    storyId: string,
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onRemove: (key: string | null) => void,
) => void;

export const subscribeToStory: SubscribeToStory = (storyId, onAdd, onChange, onRemove) => {
    const storyRef = ref(database, `story/${storyId}`);
    subscribeToRef(storyRef, onAdd, onChange, onRemove)
}

type UnsubscribeFromStory = (storyId: string) => void;

export const unsubscribeFromStory: UnsubscribeFromStory = (storyId) => {
    const storyRef = ref(database, `story/${storyId}`);
    off(storyRef);
}

type UseStory = (storyId: string) => {
    story: StoryState | undefined,
    refresh: number
}

export const useStory: UseStory = (storyId) => {
    const [refresh, setRefresh] = useState<number>(Date.now());
    const story = useRef<StoryState>();

    useEffect(() => {
        const onAdd = (key: any, val: any) => {
            if (!story.current) return;
            story.current = {
                ...story.current,
                [key]: val
            };
            setRefresh(Date.now());
        }
        const onChange = (key: any, val: any) => {
            if (!story.current) return;
            story.current = {
                ...story.current,
                [key]: val
            };
            setRefresh(Date.now());
        }
        const onRemove = (key: any) => {
            if (!story.current) return;
            story.current = {
                ...story.current,
                [key]: undefined
            };
            setRefresh(Date.now());
        }
        getStory(storyId).then(data => {
            story.current = data;
            subscribeToStory(storyId, onAdd, onChange, onRemove);
        })
        return () => {
            unsubscribeFromStory(storyId);
        }
    }, [storyId, setRefresh]);
    return {
        story: story.current, 
        refresh
    }
}

type CreateExport = (
    projectId: string, 
    dimensions: Dimensions,
    storyId?: string,
) => ExportState | undefined;

export const createExport: CreateExport = (projectId, dimensions, storyId) => {
    try {
        const exportsRef = ref(database, `exports/${projectId}`);
        const exportRef = push(exportsRef);
        const exportId = exportRef.key;
        if (exportId) {
            const exportState: ExportState = { 
                projectId,
                exportId,
                dimensions,
                startTime: Date.now(),
            };
            if (storyId) {
                exportState.storyId = storyId;
            }
            set(exportRef, exportState);
            return exportState;
        }
    } catch (e) {
        console.error(e);
    }
    return undefined;
}

type SubscribeToExports = (
    projectId: string,
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onRemove: (key: string | null) => void,
) => void;

export const subscribeToExports: SubscribeToExports = (projectId, onAdd, onChange, onRemove) => {
    const exportsRef = ref(database, `exports/${projectId}`);
    subscribeToRef(exportsRef, onAdd, onChange, onRemove);
}

type UnsubscribeFromExports = (projectId: string) => void;

export const unsubscribeFromExports: UnsubscribeFromExports = (projectId) => {
    const exportsRef = ref(database, `exports/${projectId}`);
    off(exportsRef);
}

type UseExports = (projectId: string) => {
    exports: ExportState[],
    refresh: number
}

export const useExports: UseExports = (projectId) => {
    const [refresh, setRefresh] = useState<number>(Date.now());
    const exports = useRef<ExportState[]>([]);
    useEffect(() => {
        const onAdd = (_: any, val: any) => {
            exports.current.push(val);
            setRefresh(Date.now());
        }
        const onChange = (key: any, val: any) => {
            const _exports: any = [];
            for (let i = 0; i < exports.current.length; i++) {
                const exp = exports.current[i];
                if (exp.exportId === key) {
                    _exports.push(val);
                    continue;
                }
                _exports.push(exp);
            }
            exports.current = _exports;
            setRefresh(Date.now());
        }
        const onRemove = (key: any) => {
            const _exports: any = [];
            for (let i = 0; i < exports.current.length; i++) {
                const exp = exports.current[i];
                if (exp.exportId === key) {
                    continue;
                }
                _exports.push(exp);
            }
            exports.current = _exports;
            setRefresh(Date.now());
        }
        subscribeToExports(projectId, onAdd, onChange, onRemove);
        return () => {
            unsubscribeFromExports(projectId);
        }
    }, [projectId, setRefresh])
    return {
        exports: exports.current, 
        refresh
    };
}


type SubscribeToExportQueue = (
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onAdonRemoved: (key: string | null) => void,
) => void;

export const subscribeToExportQueue: SubscribeToExportQueue = (onAdd, onChange, onRemove) => {
    const exportQueueRef = ref(database, `export_queue`);
    console.log('subscribeToExportQueue')
    subscribeToRef(exportQueueRef, onAdd, onChange, onRemove);
}

type UnsubscribeFromExportQueue = () => void;

export const unsubscribeFromExportQueue: UnsubscribeFromExportQueue = () => {
    const exportQueueRef = ref(database, `export_queue`);
    off(exportQueueRef);
}


type RemoveFromExportQueue = (key: string) => void;

export const removeFromExportQueue: RemoveFromExportQueue = (key) => {
    const exportQueueRef = ref(database, `export_queue/${key}`);
    remove(exportQueueRef);
}

type UpdateExport = (exportState: ExportState) => void;

export const updateExport: UpdateExport = (exportState) => {
    try {
        const exportRef = ref(database, `exports/${exportState.projectId}/${exportState.exportId}`);
        set(exportRef, exportState);
    } catch (e) {
        console.error(e);
    }
}

type DeleteExport = (
    projectId: string,
    exportId: string,
) => Promise<boolean>;

export const deleteExport: DeleteExport = (projectId, exportId) => {
    return new Promise(async (resolve, reject) => {
        const isOwner = await isProjectOwner(projectId);
        if (!isOwner) {
            reject("Only the project owner can delete project exports");
            return;
        }
        const exportsRef = ref(database, `exports/${projectId}/${exportId}`);
        set(exportsRef, null);
        resolve(true);
    })
}

type DeleteProjectExports = (
    projectId: string,
) => Promise<boolean>;

export const deleteProjectExports: DeleteProjectExports = (projectId) => {
    return new Promise(async (resolve, reject) => {
        const isOwner = await isProjectOwner(projectId);
        if (!isOwner) {
            reject("Only the project owner can delete project exports");
            return;
        }
        const exportsRef = ref(database, `exports/${projectId}`);
        remove(exportsRef);
        resolve(true);
    })
}


type CreateWorker = () => string | null;

export const createWorker: CreateWorker = () => {
    try {
        const workersRef = ref(database, `workers/`);
        const workerRef = push(workersRef);
        set(workerRef, {
            timestamp: Date.now()
        });
        return workerRef.key;
    } catch (e) {
        console.error(e);
    }
    return null;
}

type UpdateWorkerTimestamp = (
    workerId: string
) => string | null;

export const updateWorkerTimestamp: UpdateWorkerTimestamp = (workerId) => {
    try {
        const workerRef = ref(database, `workers/${workerId}`);
        set(workerRef, {
            timestamp: Date.now()
        });
        return workerRef.key;
    } catch (e) {
        console.error(e);
    }
    return null;
}

type SubscribeToWorkers = (
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onAdonRemoved: (key: string | null) => void,
) => void;

export const subscribeToWorkers: SubscribeToWorkers = (onAdd, onChange, onRemove) => {
    const workersRef = ref(database, `workers`);
    console.log('subscribeToWorkers')
    subscribeToRef(workersRef, onAdd, onChange, onRemove);
}

type UnsubscribeFromWorkers = () => void;

export const unsubscribeFromWorkers: UnsubscribeFromWorkers = () => {
    const workersRef = ref(database, `workers`);
    off(workersRef);
}


type RemoveWorker = (workerId: string) => void;

export const removeWorker: RemoveWorker = async (workerId) => {
    const workerRef = ref(database, `workers/${workerId}`);
    await remove(workerRef);
    const workerQueueRef = ref(database, `workers/${workerId}`);
    await remove(workerQueueRef);
}

type Worker = {
    timestamp: number;
}

type GetWorkers = () => Promise<Worker[]>;

export const getWorkers: GetWorkers = async () => {
    const workerRef = ref(database, `workers/`);
    const snapshot = await get(workerRef);
    if (snapshot.exists()) {
        const data = snapshot.val();
        return Object.values(data) as Worker[];
    }
    return [];
}

type GetWorker = (workerId: string) => Promise<Worker | null>;

export const getWorker: GetWorker = async (workerId) => {
    const workerRef = ref(database, `workers/${workerId}`);
    const snapshot = await get(workerRef);
    if (snapshot.exists()) {
        return snapshot.val() as Worker;
    }
    return null;
}

type SubscribeToWorkerQueue = (
    workerId: string,
    onAdd: (key: string | null, val: any) => void,
    onChange: (key: string | null, val: any) => void,
    onAdonRemoved: (key: string | null) => void,
) => void;

export const subscribeToWorkerQueue: SubscribeToWorkerQueue = (
    workerId,
    onAdd, 
    onChange, 
    onRemove
) => {
    const workerQueueRef = ref(database, `worker_queue/${workerId}`);
    console.log('subscribeToWorkerQueue')
    subscribeToRef(workerQueueRef, onAdd, onChange, onRemove);
}

type UnsubscribeFromWorkerQueue = (workerId: string) => void;

export const unsubscribeFromWorkerQueue: UnsubscribeFromWorkerQueue = (
    workerId
) => {
    const workerQueueRef = ref(database, `worker_queue/${workerId}`);
    off(workerQueueRef);
}

type RemoveFromWorkerQueue = (workerId: string, key: string) => void;

export const removeFromWorkerQueue: RemoveFromWorkerQueue = (workerId, key) => {
    const workerQueueRef = ref(database, `worker_queue/${workerId}/${key}`);
    remove(workerQueueRef);
}

type AddToWorkerQueue = (
    workerId: string,
    projectState: ProjectState
) => string | null | undefined;

export const addToWorkerQueue: AddToWorkerQueue = (workerId, projectState) => {
    try {
        const workerQueueRef = ref(database, `worker_queue/${workerId}`);
        const queueRef = push(workerQueueRef);
        set(queueRef, projectState);
        return queueRef.key;
    } catch (e) {
        console.error(e);
    }
    return undefined;
}